import React from 'react';
import { Button, Box, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  ShoppingCart as ShoppingCartIcon,
  ShoppingBag as ShoppingBagIcon,
  CreditCard as CreditCardIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

const StyledButton = styled(Button)(({ buttonVariant, buttonSize }) => {
  // Size configurations - Mobile-First with Big Tappable Buttons
  const sizeStyles = {
    small: {
      padding: '10px 20px',
      fontSize: '0.9rem',
      minHeight: '48px', // Increased for better mobile touch
      fontWeight: 700,
    },
    medium: {
      padding: '16px 32px',
      fontSize: '1.1rem',
      minHeight: '56px', // Larger mobile touch target
      fontWeight: 700,
    },
    large: {
      padding: '20px 40px',
      fontSize: '1.25rem',
      minHeight: '64px', // Big touch target
      fontWeight: 800,
    },
    xlarge: {
      padding: '24px 48px',
      fontSize: '1.4rem',
      minHeight: '72px', // Very large for hero buttons
      fontWeight: 800,
    },
  };

  // Variant styles - Sporty, High-Contrast Design
  const variantStyles = {
    primary: {
      background: 'linear-gradient(135deg, var(--primary) 0%, #000000 100%)',
      color: 'white',
      boxShadow: '0 6px 25px color-mix(in srgb, var(--primary) 40%, transparent)',
      fontWeight: 800,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
      '&:hover': {
        background: 'linear-gradient(135deg, #000000 0%, var(--primary) 100%)',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.5)',
        transform: 'translateY(-3px) scale(1.02)',
      },
      '&:active': {
        transform: 'translateY(-1px) scale(0.98)',
        boxShadow: '0 4px 15px color-mix(in srgb, var(--primary) 30%, transparent)',
      },
    },
    secondary: {
      backgroundColor: '#ffffff',
      color: '#000000',
      border: '3px solid #000000',
      fontWeight: 700,
      letterSpacing: '0.3px',
      textTransform: 'uppercase',
      '&:hover': {
        backgroundColor: '#000000',
        color: '#ffffff',
        borderColor: 'var(--primary)',
        transform: 'translateY(-2px)',
      },
    },
    success: {
      backgroundColor: '#4caf50',
      color: 'white',
      '&:hover': {
        backgroundColor: '#45a049',
      },
    },
    danger: {
      backgroundColor: '#f44336',
      color: 'white',
      '&:hover': {
        backgroundColor: '#da190b',
      },
    },
  };

  return {
    ...sizeStyles[buttonSize] || sizeStyles.medium,
    ...variantStyles[buttonVariant] || variantStyles.primary,
    borderRadius: '12px', // More modern rounded corners
    fontFamily: '"Inter", "Roboto", sans-serif', // Modern, clean font
    fontWeight: 800, // Bold for sporty feel
    textTransform: 'uppercase',
    letterSpacing: '0.8px', // Spaced out for sporty look
    transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)', // Smooth, bouncy transition
    position: 'relative',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    userSelect: 'none',
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      transform: 'none !important',
    },
    // Ripple effect
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: 0,
      height: 0,
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.5)',
      transform: 'translate(-50%, -50%)',
      transition: 'width 0.6s, height 0.6s',
    },
    '&:active::before': {
      width: '300px',
      height: '300px',
    },
  };
});

const CTAButton = ({
  variant = 'primary',
  icon = 'none',
  loading = false,
  fullWidth = false,
  size = 'medium',
  children,
  disabled,
  onClick,
  ...props
}) => {
  // Icon mapping
  const iconMap = {
    cart: <ShoppingCartIcon />,
    buy: <ShoppingBagIcon />,
    checkout: <CreditCardIcon />,
    success: <CheckCircleIcon />,
    arrow: <ArrowForwardIcon />,
    none: null,
  };

  const selectedIcon = iconMap[icon];

  return (
    <StyledButton
      buttonVariant={variant}
      buttonSize={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      onClick={onClick}
      startIcon={loading ? null : selectedIcon}
      {...props}
    >
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={20} color="inherit" />
          Processing...
        </Box>
      ) : (
        children
      )}
    </StyledButton>
  );
};

// Preset CTA variations for common use cases
export const AddToCartButton = (props) => (
  <CTAButton icon="cart" {...props}>
    {props.children || 'Add to Cart'}
  </CTAButton>
);

export const BuyNowButton = (props) => (
  <CTAButton icon="buy" variant="primary" {...props}>
    {props.children || 'Buy Now'}
  </CTAButton>
);

export const CheckoutButton = (props) => (
  <CTAButton icon="checkout" size="large" {...props}>
    {props.children || 'Proceed to Checkout'}
  </CTAButton>
);

export const ContinueShoppingButton = (props) => (
  <CTAButton variant="secondary" icon="arrow" {...props}>
    {props.children || 'Continue Shopping'}
  </CTAButton>
);

export default CTAButton;